import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight, Star } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_facility-experts-12/artifacts/a0e4sdha_zemka%202.webp";

const lineVariants = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLanguage();

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-end overflow-hidden bg-[#0F172A]"
    >
      <motion.div
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.4, ease: "easeOut" }}
        className="absolute inset-0 bg-cover bg-center will-change-transform"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A]/95 via-[#0F172A]/75 to-[#0F172A]/95" aria-hidden="true" />
      <div className="absolute inset-0 bg-grid-slate opacity-40" aria-hidden="true" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8 pt-32 pb-20 lg:pb-28">
        <motion.div initial="hidden" animate="show" className="max-w-4xl">
          <motion.div
            custom={0}
            variants={lineVariants}
            className="inline-flex items-center gap-2 border border-cyan-400/40 bg-cyan-400/10 px-3 py-1.5 mb-8"
            data-testid="hero-badge"
          >
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <motion.span
                  key={i}
                  initial={{ scale: 0, rotate: -30 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.5 + i * 0.08, type: "spring", stiffness: 260, damping: 18 }}
                >
                  <Star size={12} className="fill-amber-400 text-amber-400" />
                </motion.span>
              ))}
            </div>
            <span className="text-xs font-semibold tracking-wider uppercase text-cyan-300">{t.hero.badge}</span>
          </motion.div>

          <h1 className="font-display text-white text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.02] tracking-tight overflow-hidden">
            <motion.span custom={1} variants={lineVariants} className="block">
              {t.hero.title1}
            </motion.span>
            <motion.span custom={2} variants={lineVariants} className="block">
              {t.hero.title2}
            </motion.span>
            <motion.span custom={3} variants={lineVariants} className="block text-cyan-400">
              {t.hero.title3}
            </motion.span>
          </h1>

          <motion.p
            custom={4}
            variants={lineVariants}
            className="mt-8 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
            data-testid="hero-subtitle"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div custom={5} variants={lineVariants} className="mt-10 flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              className="group inline-flex items-center justify-center gap-2 bg-[#06B6D4] hover:bg-[#0891B2] text-white font-semibold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgba(6,182,212,0.5)]"
              data-testid="hero-cta-quote"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
            <a
              href="tel:01708251779"
              className="inline-flex items-center justify-center gap-3 border-2 border-white/30 hover:border-white text-white font-semibold px-8 py-4 transition-all duration-300 backdrop-blur-sm bg-white/5 hover:bg-white/10 hover:-translate-y-0.5"
              data-testid="hero-cta-call"
            >
              <Phone size={18} />
              {t.hero.ctaSecondary}
            </a>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="mt-16 lg:mt-24 grid grid-cols-3 gap-px bg-white/10 border border-white/10 max-w-3xl"
          data-testid="hero-stats"
        >
          {[
            { v: t.hero.stat1, l: t.hero.stat1Label },
            { v: t.hero.stat2, l: t.hero.stat2Label },
            { v: t.hero.stat3, l: t.hero.stat3Label },
          ].map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 + i * 0.1 }}
              className="bg-[#0F172A] p-6 lg:p-8"
            >
              <div className="font-display text-3xl lg:text-4xl font-bold text-cyan-400">{s.v}</div>
              <div className="mt-1 text-xs sm:text-sm text-slate-400 leading-tight">{s.l}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden sm:flex flex-col items-center gap-2 text-white/50"
        aria-hidden="true"
      >
        <span className="text-[10px] font-semibold tracking-[0.3em] uppercase">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="h-8 w-px bg-gradient-to-b from-white/60 to-transparent"
        />
      </motion.div>
    </section>
  );
};
